import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchProfile } from '../services/profileService';
import { createCheckoutSession, claimPendingSubscription } from '../services/stripeService';
import { isPaidSubscriptionActive } from '../lib/subscription';
import LoadingSpinner from './LoadingSpinner';
import './SubscribePage.css';

const SubscribePage: React.FC = () => {
  const { user, session, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const canceled = searchParams.get('stripe') === 'canceled';
  const isTrialing = subscriptionStatus === 'trialing';

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        if (session?.access_token) {
          try {
            const claim = await claimPendingSubscription(session.access_token);
            if (claim.claimed) {
              if (!cancelled) {
                setSubscriptionStatus('trialing');
                setLoading(false);
              }
              return;
            }
          } catch (claimErr) {
            console.warn('SubscribePage: claim pending failed', claimErr);
          }
        }
        const profile = await fetchProfile(user.id);
        if (!cancelled) {
          setSubscriptionStatus(profile?.subscription_status ?? null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError('Could not load your account.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, session?.access_token]);

  const handleSubscribe = async () => {
    if (!session?.access_token) {
      setError('You must be signed in.');
      return;
    }
    setError(null);
    setStarting(true);
    try {
      const result = await createCheckoutSession(session.access_token);
      if (result.claimed) {
        window.location.href = result.url || '/dashboard';
        return;
      }
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setStarting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-inner">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isPaidSubscriptionActive(subscriptionStatus)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-inner">
        <h1 className="subscribe-title">Subscribe to GallopAI</h1>
        <p className="subscribe-lead">
          {isTrialing
            ? 'Subscribe now to keep uninterrupted access when your free trial ends.'
            : 'Your free trial has ended. Subscribe for £30/month to regain full access.'}
        </p>

        {canceled && (
          <div className="subscribe-banner subscribe-banner-warn">
            Checkout was canceled. You can try again whenever you are ready.
          </div>
        )}

        {error && <div className="subscribe-banner subscribe-banner-error">{error}</div>}

        <div className="subscribe-card">
          <div className="subscribe-price">
            <span className="subscribe-amount">£30</span>
            <span className="subscribe-period">/month</span>
          </div>
          <p className="subscribe-vat">Price includes VAT where applicable.</p>

          <ul className="subscribe-features">
            <li>
              <Check size={18} aria-hidden />
              Unlimited access to the app while subscribed
            </li>
            <li>
              <Check size={18} aria-hidden />
              Connect your Betfair Exchange account
            </li>
            <li>
              <Check size={18} aria-hidden />
              Manage billing and invoices in the customer portal
            </li>
          </ul>

          <button
            type="button"
            className="subscribe-cta"
            onClick={handleSubscribe}
            disabled={starting}
          >
            {starting
              ? 'Redirecting…'
              : isTrialing
                ? 'Subscribe now — £30/month'
                : 'Continue to secure checkout — £30/month'}
          </button>

          <p className="subscribe-footnote">
            You can also manage billing later from{' '}
            <Link to="/settings">Settings</Link> once subscribed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
